/**
 * Mode course — EF-MOD-001 à EF-MOD-008, EF-IHM-003, EF-CAL-007.
 *
 * Pas de liste : un POI par carte, plein écran, la première étant le POI mis
 * en évidence. Une liste, même à taille augmentée, force à choisir entre voir
 * loin et voir gros ; en roulant, la seule question ouverte porte sur le
 * prochain point. On passe d'une carte à la suivante par un glissement
 * vertical avec accrochage, un geste par POI, dans les deux sens.
 *
 * Aucune action destructive n'est atteignable en moins de deux gestes.
 */

import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { nowLocalDateTime, type LocalDateTime } from '../../core/local-datetime';
import { elevationGainAtProgression } from '../../geo/track';
import { PositionWatcher, type PositionOutcome, type ValidPosition } from '../../platform/geolocation';
import { ScreenWakeLock, type WakeLockStatus } from '../../platform/wake-lock';
import {
  describeOffRoute,
  highlightedLine,
  isOnRoute,
  orderedUpcoming,
  RaceProjector,
  recomputeForRace,
  shouldRecompute,
  type OffRouteReport,
  type ProjectionCandidate,
} from '../../roadbook/race';
import type { PoiLineRef } from '../../roadbook/order';
import { MarkerList } from '../components/MarkerList';
import { RaceActionsSheet } from '../components/RaceActionsSheet';
import { RaceCard } from '../components/RaceCard';
import { Glyph } from '../icons';
import { formatBearing, formatDistanceToRoute, formatKilometers } from '../format';
import { ACTIONS, ERRORS } from '../labels';
import {
  busy,
  document_,
  isStale,
  notify,
  openSheet,
  parameters,
  raceState,
  ridingCurve,
  saveRaceProgress,
  setRaceState,
  sheet,
} from '../state';

/** Durée de l'appui long de verrouillage et de déverrouillage. */
const HOLD_MS = 2000;

/** Profondeur de la file : au-delà, les estimations ne décident plus rien. */
const QUEUE_LENGTH = 8;

export function RaceScreen(): JSX.Element {
  const current = document_.value;
  const race = raceState.value;

  const projector = useRef<RaceProjector | null>(null);
  const lastComputeAt = useRef<number | null>(null);
  const lastProgression = useRef<number | null>(null);

  const [, setWakeStatus] = useState<WakeLockStatus>('libéré');
  const [position, setPosition] = useState<ValidPosition | null>(null);
  const [offRoute, setOffRoute] = useState<OffRouteReport | null>(null);
  const [candidates, setCandidates] = useState<readonly ProjectionCandidate[]>([]);
  const [futureArrival, setFutureArrival] = useState<(progression: number) => LocalDateTime | null>(
    () => (): LocalDateTime | null => null,
  );
  const [screenReaderMode] = useState(() => detectScreenReader());

  const canEnter = current !== null && current.results.totals !== null && busy.value === null;

  useEffect(() => {
    if (!canEnter) return undefined;

    const stored = current?.results.raceState ?? null;
    projector.current = new RaceProjector({
      lastProgressionMeters: stored?.lastProgressionMeters ?? null,
      lastAcceptedAtMs: stored === null ? null : Date.now(),
    });

    const instance = new PositionWatcher({ onOutcome: handleOutcome });
    instance.start();

    const lock = new ScreenWakeLock(setWakeStatus);
    if (parameters.value.device.keepScreenAwake) void lock.request();

    setRaceState({ active: true });

    return () => {
      instance.stop();
      void lock.release();
      setRaceState({ active: false, locked: false });
      // La position est supprimée de la mémoire fonctionnelle à la sortie.
      setPosition(null);
      setOffRoute(null);
      projector.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEnter]);

  function handleOutcome(outcome: PositionOutcome): void {
    const document = document_.value;
    const instance = projector.current;
    if (document === null || instance === null) return;

    if (outcome.kind === 'invalid') {
      // Aucun recalcul automatique ; les dernières estimations restent visibles.
      setRaceState({ positionProblem: outcome.message });
      setPosition(null);
      return;
    }

    setRaceState({ positionProblem: null });
    setPosition(outcome.position);

    const track = document.route.track;
    const projected = instance.project(track, outcome.position.coordinate, outcome.position.timestampMs);
    if (projected === null) return;

    if (projected.kind === 'ambiguous') {
      setCandidates(projected.candidates);
      setRaceState({ awaitingChoice: projected.candidates.map(toChoice) });
      return;
    }
    setCandidates([]);
    setRaceState({ awaitingChoice: [] });

    if (projected.kind === 'pendingConfirmation') {
      setRaceState({ pendingConfirmationOfJump: true });
      return;
    }
    setRaceState({ pendingConfirmationOfJump: false });

    const onRoute = isOnRoute(projected.candidate.distanceToRouteMeters, outcome.position.accuracyMeters);
    if (!onRoute) {
      setOffRoute(describeOffRoute(track, outcome.position.coordinate, instance.progressionMeters));
      setRaceState({ offRoute: true });
      return;
    }
    setOffRoute(null);
    setRaceState({ offRoute: false });

    maybeRecompute(projected.candidate.progressionMeters);
  }

  function maybeRecompute(progressionMeters: number, force = false): void {
    const curve = ridingCurve();
    if (curve === null) return;
    const nowMs = Date.now();
    if (!force && !shouldRecompute(lastComputeAt.current, lastProgression.current, nowMs, progressionMeters)) {
      return;
    }

    const now = nowLocalDateTime();
    const result = recomputeForRace({
      curve,
      schedule: parameters.value.schedule,
      progressionMeters,
      now,
      includeSleep: parameters.value.schedule.sleep.enabled,
    });

    lastComputeAt.current = nowMs;
    lastProgression.current = progressionMeters;
    setRaceState({ lastComputedAtLocal: now });
    setFutureArrival(() => (progression: number): LocalDateTime | null => {
      if (progression < progressionMeters) return null;
      return result.estimatedArrival(progression);
    });
    // Seule la dernière progression retenue est conservée, sans coordonnée.
    saveRaceProgress(progressionMeters, now);
  }

  const correctPosition = (): void => {
    projector.current?.reset();
    lastComputeAt.current = null;
    lastProgression.current = null;
    setCandidates([]);
    setRaceState({ awaitingChoice: [], pendingConfirmationOfJump: false });
    notify('info', 'Position réinitialisée : la recherche reprend sur la totalité du parcours.');
  };

  const recomputeNow = (): void => {
    const progression = projector.current?.progressionMeters;
    if (progression != null) maybeRecompute(progression, true);
  };

  if (current === null) {
    return (
      <div class="screen race">
        <p>Aucun roadbook chargé.</p>
        <RaceFooter
          locked={false}
          screenReaderMode={screenReaderMode}
          hasPosition={false}
          onCorrectPosition={() => {}}
          onRecompute={() => {}}
        />
      </div>
    );
  }

  if (current.results.totals === null) {
    return (
      <div class="screen race">
        <p class="notice">{ERRORS.raceNeedsResult}</p>
        <RaceFooter
          locked={false}
          screenReaderMode={screenReaderMode}
          hasPosition={false}
          onCorrectPosition={() => {}}
          onRecompute={() => {}}
        />
      </div>
    );
  }

  if (busy.value !== null) {
    return (
      <div class="screen race">
        <p class="notice">{ERRORS.raceDuringOperation}</p>
        <RaceFooter
          locked={false}
          screenReaderMode={screenReaderMode}
          hasPosition={false}
          onCorrectPosition={() => {}}
          onRecompute={() => {}}
        />
      </div>
    );
  }

  const crossed = projector.current?.crossedProgressionMeters ?? 0;
  const currentProgression = projector.current?.progressionMeters ?? null;
  const track = current.route.track;

  const highlight = highlightedLine({
    pois: current.pois,
    display: parameters.value.display,
    crossedProgressionMeters: crossed,
    stops: current.results.stops,
  });

  const gainHere =
    currentProgression === null || !track.elevation.available
      ? null
      : elevationGainAtProgression(track, currentProgression);

  const queue: readonly PoiLineRef[] =
    highlight.kind === 'poi'
      ? orderedUpcoming(current.pois, parameters.value.display, crossed).slice(0, QUEUE_LENGTH)
      : [];

  const startLocal = current.results.totals.startLocal;

  return (
    <div class={race.locked ? 'screen race race--locked' : 'screen race'}>
      {/*
        Bandeau d'état : marqueurs permanents, plus les deux seules commandes
        qui ne concernent aucune carte en particulier. Chacune est adjacente
        au marqueur qu'elle corrige.
      */}
      <div class="race-status" role="status" aria-live="polite">
        {race.pendingConfirmationOfJump ? (
          <span class="status-badge">Position en cours de confirmation</span>
        ) : null}
        <MarkerList document={current} stale={isStale.value} compact />
      </div>

      <div class="race-body">
        {race.awaitingChoice.length > 0 ? (
          <section class="race-card race-card--next">
            <h2>Position ambiguë</h2>
            <p>Plusieurs points du parcours correspondent à votre position. Désignez le vôtre.</p>
            <div class="actions actions--stacked">
              {candidates.map((candidate) => (
                <button
                  key={candidate.progressionMeters}
                  type="button"
                  onClick={() => {
                    projector.current?.accept(candidate, Date.now());
                    setCandidates([]);
                    setRaceState({ awaitingChoice: [] });
                    maybeRecompute(candidate.progressionMeters, true);
                  }}
                >
                  km {formatKilometers(candidate.progressionMeters)} — à{' '}
                  {formatDistanceToRoute(candidate.distanceToRouteMeters)} de la trace
                </button>
              ))}
            </div>
          </section>
        ) : race.offRoute && offRoute !== null ? (
          <section class="race-card race-card--next">
            <h2>Hors parcours</h2>
            <p class="muted">
              Ces informations ne constituent ni un itinéraire de retour, ni une instruction de navigation.
            </p>
            <dl class="definition-list">
              <div>
                <dt>Distance au point le plus proche</dt>
                <dd>{formatDistanceToRoute(offRoute.distanceMeters)}</dd>
              </div>
              <div>
                <dt>Cap vers ce point</dt>
                <dd>{formatBearing(offRoute.bearingDegrees)}</dd>
              </div>
              <div>
                <dt>Progression de ce point</dt>
                <dd>
                  km {formatKilometers(offRoute.progressionMeters)} —{' '}
                  {offRoute.ahead ? 'en avant' : 'en arrière'}
                </dd>
              </div>
            </dl>
          </section>
        ) : highlight.kind === 'noPoi' ? (
          <p class="race-empty">Aucun POI dans ce roadbook.</p>
        ) : highlight.kind === 'allExcludedOrHidden' ? (
          <div class="race-empty">
            <p>
              Tous les POI restants sont{highlight.excludedCount > 0 ? ' exclus' : ''}
              {highlight.excludedCount > 0 && highlight.hiddenCount > 0 ? ' ou' : ''}
              {highlight.hiddenCount > 0 ? ' masqués par un filtre' : ''}.
            </p>
            <button
              type="button"
              onClick={() => {
                openSheet('filters');
              }}
            >
              Ouvrir les filtres
            </button>
          </div>
        ) : highlight.kind === 'finish' ? (
          <section class="race-card race-card--next">
            <div class="race-card__label">
              <span>Arrivée</span>
            </div>
            <dl class="race-card__metrics">
              <div class="race-metric">
                <dt>Distance</dt>
                <dd class="race-metric__value">
                  {formatKilometers(Math.max(0, track.lengthMeters - crossed))} km
                </dd>
              </div>
            </dl>
          </section>
        ) : (
          <>
            {/*
              Accrochage vertical confié au moteur de rendu : un geste, un POI,
              dans les deux sens.
            */}
            <div class="race-track">
              {queue.map((line, rank) => {
                const poi = current.pois.get(line.poiId);
                if (poi === undefined) return null;
                const previous = queue[rank - 1];
                const arrival = futureArrival(line.progressionMeters);
                const passage = poi.passages.find((item) => item.index === line.passageIndex);
                const gainThere = passage?.computed?.elevationGainFromStartMeters ?? null;
                return (
                  <RaceCard
                    key={`${line.poiId}#${line.passageIndex}`}
                    poi={poi}
                    document={current}
                    passageIndex={line.passageIndex}
                    rank={rank}
                    total={queue.length}
                    remainingMeters={
                      currentProgression === null
                        ? null
                        : Math.max(0, line.progressionMeters - currentProgression)
                    }
                    remainingGainMeters={
                      gainThere === null || gainHere === null ? null : Math.max(0, gainThere - gainHere)
                    }
                    remainingSeconds={arrival === null ? null : Math.max(0, arrival - nowLocalDateTime())}
                    arrival={arrival}
                    startLocal={startLocal}
                    sameSpot={
                      previous !== undefined &&
                      Math.abs(previous.progressionMeters - line.progressionMeters) < 1
                    }
                  />
                );
              })}
            </div>
            {highlight.hiddenBefore > 0 ? (
              <p class="muted race-hint">
                {highlight.hiddenBefore} POI masqué(s) par un filtre avant celui-ci.
              </p>
            ) : null}
          </>
        )}
      </div>

      <RaceFooter
        locked={race.locked}
        screenReaderMode={screenReaderMode}
        hasPosition={position !== null}
        onCorrectPosition={correctPosition}
        onRecompute={recomputeNow}
      />
    </div>
  );
}

/**
 * Pied d'écran : le déclencheur des actions (retour au roadbook, position),
 * l'icône de statut de la position et la zone de verrouillage. Rien d'autre —
 * les commandes accessoires ne doivent pas occuper la place des informations
 * de course.
 */
function RaceFooter(props: {
  readonly locked: boolean;
  readonly screenReaderMode: boolean;
  readonly hasPosition: boolean;
  readonly onCorrectPosition: () => void;
  readonly onRecompute: () => void;
}): JSX.Element {
  const race = raceState.value;
  return (
    <div class="race-foot">
      <button
        type="button"
        class="race-leave"
        disabled={props.locked}
        onClick={() => {
          openSheet('raceActions');
        }}
      >
        <Glyph name="actions" label="" size={24} decorative />
        <span>Actions</span>
      </button>
      <span
        role="img"
        class={race.positionProblem === null ? 'position-status position-status--ok' : 'position-status position-status--ko'}
        aria-label={race.positionProblem === null ? 'Position acceptée' : `Position refusée : ${race.positionProblem}`}
      >
        <Glyph name={race.positionProblem === null ? 'position-ok' : 'position-ko'} label="" size={22} decorative />
      </span>
      <LockControl
        locked={props.locked}
        screenReaderMode={props.screenReaderMode}
        onToggle={(locked) => {
          setRaceState({ locked });
        }}
      />
      {sheet.value === 'raceActions' ? (
        <RaceActionsSheet
          hasPosition={props.hasPosition}
          onCorrectPosition={props.onCorrectPosition}
          onRecompute={props.onRecompute}
        />
      ) : null}
    </div>
  );
}

function toChoice(candidate: ProjectionCandidate): {
  progressionMeters: number;
  distanceToRouteMeters: number;
} {
  return {
    progressionMeters: candidate.progressionMeters,
    distanceToRouteMeters: candidate.distanceToRouteMeters,
  };
}

/**
 * Zone de verrouillage : appui long d'au moins 2 secondes sur une zone d'au
 * moins 52 pixels CSS, de **position et de dimensions invariantes** — une zone
 * qui se déplace en changeant d'état se dérobe sous le doigt. Lorsqu'un
 * lecteur d'écran est actif, elle se comporte comme un bouton standard.
 */
function LockControl(props: {
  readonly locked: boolean;
  readonly screenReaderMode: boolean;
  readonly onToggle: (locked: boolean) => void;
}): JSX.Element {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [holding, setHolding] = useState(false);

  const cancel = (): void => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    setHolding(false);
  };

  const label = props.locked ? ACTIONS.unlockScreen : ACTIONS.lockScreen;

  if (props.screenReaderMode) {
    return (
      <button
        type="button"
        class="lock-zone"
        aria-label={label}
        onClick={() => {
          props.onToggle(!props.locked);
        }}
      >
        <Glyph name={props.locked ? 'verrou' : 'verrou-ouvert'} label="" size={26} decorative />
      </button>
    );
  }

  return (
    <button
      type="button"
      class={holding ? 'lock-zone lock-zone--holding' : 'lock-zone'}
      aria-label={`${label} — appui long de deux secondes`}
      onPointerDown={() => {
        setHolding(true);
        timer.current = setTimeout(() => {
          props.onToggle(!props.locked);
          cancel();
        }, HOLD_MS);
      }}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
    >
      <Glyph name={props.locked ? 'verrou' : 'verrou-ouvert'} label="" size={26} decorative />
    </button>
  );
}

/** Heuristique : un lecteur d'écran rend l'appui long impraticable. */
function detectScreenReader(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}
